import dayjs from 'dayjs'
import { RatePeriod } from '@/types/plugSessions'
import { formatHourRange } from './ChargeSessionDetails.utils'
import {
  formatCents,
  HourlyRateBar,
  RATE_PERIOD_LABELS,
} from './UpcomingRatesCard.utils'
import { useMeasuredWidth } from './useMeasuredWidth'

// Figma draws the real-time rate chart at 287×142. It is drawn at the card's
// real width (see useMeasuredWidth) so text never scales up on desktop.
const DESIGN_WIDTH = 287
const HEIGHT = 142
// Exported so the card's legend can centre on the plot, not the whole card
export const REAL_TIME_PLOT_LEFT = 32
const PLOT_LEFT = REAL_TIME_PLOT_LEFT
const CEILING_Y = 6
const BASELINE_Y = 102
// Negative-price bars stop before the time labels
const MAX_NEGATIVE_DEPTH = 16
const X_LABEL_Y = 134
const BAR_GAP = 1.5
const LINE_WIDTH = 1.81
const DASH = '3.63 3.63'

// SVG attributes can't take Tailwind classes. The green and red only exist as
// base colors (same palette as HourlyRateChart and the rates bar).
const PERIOD_FILLS: Record<RatePeriod, string> = {
  negative: 'var(--color-base-green-700)',
  low: 'var(--color-base-green-700)',
  off_peak: 'var(--color-base-green-700)',
  flat: 'var(--color-base-green-700)',
  medium: 'var(--color-orange-400)',
  partial_peak: 'var(--color-orange-400)',
  high: 'var(--color-base-red-500)',
  peak: 'var(--color-base-red-500)',
}

const COLORS = {
  gridline: 'var(--color-grey-100)',
  baseline: 'var(--color-grey-400)',
  label: 'var(--color-grey-500)',
}

const LABEL_STYLE = {
  fontSize: 10.886,
  fontWeight: 700,
  fill: COLORS.label,
}

// The hover, tap or focus target for each hour: the full plot height, so a
// short or negative bar is as easy to hit as a tall one
const HIT_TOP = 0
const HIT_BOTTOM = BASELINE_Y + MAX_NEGATIVE_DEPTH

type Props = {
  bars: HourlyRateBar[]
  yAxisTicksCents: number[]
  axisLabels: string[]
  // The hovered, focused or tapped hour; the card reads its price out
  active: number | null
  onActiveChange: (index: number | null) => void
}

export default function RealTimeRateChart({
  bars,
  yAxisTicksCents,
  axisLabels,
  active,
  onActiveChange,
}: Props) {
  const { ref, width } = useMeasuredWidth<HTMLDivElement>(DESIGN_WIDTH)
  const plotRight = width
  const ceilingCents = yAxisTicksCents[yAxisTicksCents.length - 1] || 1
  const pixelsPerCent = (BASELINE_Y - CEILING_Y) / ceilingCents
  const step = (plotRight - PLOT_LEFT) / Math.max(bars.length, 1)
  const barWidth = Math.max(step - BAR_GAP, 1)
  const toY = (cents: number) => BASELINE_Y - cents * pixelsPerCent

  const cheapest = Math.min(...bars.map((bar) => bar.cents))
  const priciest = Math.max(...bars.map((bar) => bar.cents))

  return (
    <div ref={ref} className="w-full">
      <svg
        id="charge-forecast-v2-real-time-chart"
        role="img"
        aria-label={`Hourly rates, from ${formatCents(
          cheapest,
        )} to ${formatCents(priciest)} per kWh`}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        className="block w-full h-auto overflow-visible"
        style={{ fontFamily: 'inherit' }}
        onMouseLeave={() => onActiveChange(null)}
      >
        {yAxisTicksCents.map((cents) => (
          <g key={cents}>
            <line
              x1={PLOT_LEFT}
              x2={plotRight}
              y1={toY(cents)}
              y2={toY(cents)}
              stroke={cents === 0 ? COLORS.baseline : COLORS.gridline}
              strokeWidth={LINE_WIDTH}
              strokeDasharray={cents === 0 ? undefined : DASH}
            />
            <text x={24} y={toY(cents) + 4} textAnchor="end" {...LABEL_STYLE}>
              {formatCents(cents)}
            </text>
          </g>
        ))}

        {bars.map((bar, index) => {
          const clipped = bar.cents > ceilingCents
          const drawnCents = clipped ? ceilingCents : bar.cents
          const height =
            bar.cents >= 0
              ? drawnCents * pixelsPerCent
              : Math.min(-bar.cents * pixelsPerCent, MAX_NEGATIVE_DEPTH)
          const x = PLOT_LEFT + index * step
          // A bar past the axis is drawn to the top and carries its real price
          const labelFitsRight = x + barWidth + 30 <= plotRight
          const start = dayjs(bar.start)
          return (
            <g key={bar.start}>
              <rect
                x={x}
                y={bar.cents >= 0 ? toY(drawnCents) : BASELINE_Y}
                width={barWidth}
                height={Math.max(height, 1)}
                rx={2}
                fill={PERIOD_FILLS[bar.period]}
                opacity={active !== null && active !== index ? 0.6 : 1}
                className="transition-opacity"
              />
              {/* Same hover / tap / focus behaviour as the TOU bar's segments */}
              <rect
                role="button"
                tabIndex={0}
                aria-label={`${RATE_PERIOD_LABELS[bar.period]}, ${formatCents(
                  bar.cents,
                )} per kWh, ${formatHourRange(start, start.add(1, 'hour'))}`}
                aria-pressed={active === index}
                x={x}
                y={HIT_TOP}
                width={step}
                height={HIT_BOTTOM - HIT_TOP}
                fill="transparent"
                className="cursor-pointer outline-none"
                onMouseEnter={() => onActiveChange(index)}
                onFocus={() => onActiveChange(index)}
                onBlur={() => onActiveChange(null)}
                onClick={() => onActiveChange(index)}
              />
              {clipped && (
                <text
                  x={labelFitsRight ? x + barWidth + 3 : x - 3}
                  y={CEILING_Y + 9}
                  textAnchor={labelFitsRight ? 'start' : 'end'}
                  {...LABEL_STYLE}
                  fill={PERIOD_FILLS[bar.period]}
                >
                  {formatCents(bar.cents)}
                </text>
              )}
            </g>
          )
        })}

        {axisLabels.map((label, index) => (
          <text
            key={`${label}-${index}`}
            x={
              PLOT_LEFT +
              (index / (axisLabels.length - 1)) * (plotRight - PLOT_LEFT)
            }
            y={X_LABEL_Y}
            textAnchor={
              index === 0
                ? 'start'
                : index === axisLabels.length - 1
                ? 'end'
                : 'middle'
            }
            {...LABEL_STYLE}
          >
            {label}
          </text>
        ))}
      </svg>
    </div>
  )
}
